import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Barcode, CheckCircle, Package, Warning, MagnifyingGlass, Plus } from "@phosphor-icons/react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import api from "@/lib/api";
import NumberInput from "@/components/ui/NumberInput";

const fmt = (s) => { if (!s) return "—"; try { const [y,m,d]=s.split("-"); return `${d}/${m}/${y.slice(2)}`; } catch { return s; } };

/**
 * Diálogo para elegir los lotes de materia prima consumidos al terminar un ítem.
 * - Trae sugerencias FIFO desde /api/lotes/sugerir-fifo
 * - Permite editar los kilos de cada lote y (des)tildar cuáles usar
 * - **Nuevo**: buscador libre sobre TODOS los lotes disponibles (cross-producto)
 *   para poder consumir de un lote alternativo si el operador lo necesita.
 * - Emite onConfirm(lotesConsumidos[])
 *
 * Props:
 *   open, producto, kilosNecesarios, onCancel, onConfirm(lotes)
 */
export default function SelectorLotesDialog({ open, producto, kilosNecesarios, onCancel, onConfirm }) {
  const [sugerencias, setSugerencias] = useState([]);
  const [seleccion, setSeleccion] = useState({}); // { lote_id: { checked, kilos, numero, descripcion, kilos_disponibles, fecha_elaboracion, fecha_vencimiento } }
  const [loading, setLoading] = useState(false);
  const [restanteBackend, setRestanteBackend] = useState(0);
  const [buscarQ, setBuscarQ] = useState("");
  const [lotesAlt, setLotesAlt] = useState([]);   // resultados del buscador cross-producto
  const [buscarLoading, setBuscarLoading] = useState(false);

  useEffect(() => {
    if (!open || !producto || !kilosNecesarios) return;
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/lotes/sugerir-fifo", {
          params: { producto, kilos: kilosNecesarios },
        });
        const sug = data.sugerencias || [];
        setSugerencias(sug);
        setRestanteBackend(data.restante || 0);
        // Preselección FIFO
        const sel = {};
        sug.forEach((s) => {
          sel[s.lote_id] = {
            checked: true, kilos: s.kilos_sugeridos, numero: s.numero,
            descripcion: s.descripcion, kilos_disponibles: s.kilos_disponibles,
            fecha_elaboracion: s.fecha_elaboracion, fecha_vencimiento: s.fecha_vencimiento,
          };
        });
        setSeleccion(sel);
        setBuscarQ(""); setLotesAlt([]);
      } catch {
        toast.error("Error al buscar lotes disponibles");
        setSugerencias([]);
      } finally { setLoading(false); }
    })();
  }, [open, producto, kilosNecesarios]);

  // Debounced search sobre TODOS los lotes activos (cross-producto)
  useEffect(() => {
    if (!open || buscarQ.trim().length < 2) { setLotesAlt([]); return; }
    const t = setTimeout(async () => {
      setBuscarLoading(true);
      try {
        const { data } = await api.get("/stock/lotes-disponibles", { params: { q: buscarQ, limit: 30 } });
        setLotesAlt(data.lotes || []);
      } catch { setLotesAlt([]); }
      finally { setBuscarLoading(false); }
    }, 300);
    return () => clearTimeout(t);
  }, [open, buscarQ]);

  const agregarLoteAlt = (l) => {
    if (seleccion[l.id]) { toast.info("Ese lote ya está en la selección"); return; }
    setSeleccion((prev) => ({
      ...prev,
      [l.id]: {
        checked: true, kilos: l.kilos_disponibles, numero: l.numero,
        descripcion: l.descripcion, kilos_disponibles: l.kilos_disponibles,
        fecha_elaboracion: l.fecha_elaboracion, fecha_vencimiento: l.fecha_vencimiento,
      },
    }));
    setBuscarQ(""); setLotesAlt([]);
  };

  const totales = useMemo(() => {
    const usados = Object.entries(seleccion)
      .filter(([, v]) => v.checked)
      .reduce((s, [, v]) => s + (parseFloat(v.kilos) || 0), 0);
    return { usados: +usados.toFixed(3), faltan: +Math.max(0, kilosNecesarios - usados).toFixed(3) };
  }, [seleccion, kilosNecesarios]);

  const confirmar = () => {
    const lotes = Object.entries(seleccion)
      .filter(([, v]) => v.checked && parseFloat(v.kilos) > 0)
      .map(([lote_id, v]) => ({ lote_id, numero: v.numero, kilos: parseFloat(v.kilos) }));
    onConfirm(lotes);
  };

  const saltar = () => onConfirm([]);
  const seleccionEntries = Object.entries(seleccion);

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onCancel()}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Barcode size={24} weight="duotone" className="text-[#921A0E]" />
            Lotes usados en producción
          </DialogTitle>
          <DialogDescription>
            Se necesitan <strong>{kilosNecesarios} kg</strong> de <strong>{producto}</strong>.
            Sugerencia FIFO abajo. Podés ajustar los kilos, agregar otros lotes o buscar cualquier lote disponible.
          </DialogDescription>
        </DialogHeader>

        {loading && <div className="py-6 text-neutral-500 text-sm text-center">Buscando lotes…</div>}

        {!loading && seleccionEntries.length === 0 && sugerencias.length === 0 && (
          <div className="bg-amber-50 border border-amber-200 rounded-md p-4 text-sm text-amber-900 flex items-start gap-2">
            <Warning size={18} className="text-amber-600 mt-0.5" />
            <div>
              <div className="font-medium">No hay lotes sugeridos para "{producto}"</div>
              <div className="text-xs mt-1">
                Podés buscar cualquier otro lote disponible abajo o terminar sin registrar consumo.
              </div>
            </div>
          </div>
        )}

        {!loading && seleccionEntries.length > 0 && (
          <div className="space-y-2 max-h-72 overflow-y-auto" data-testid="lotes-sugerencias">
            {seleccionEntries.map(([lote_id, sel]) => (
              <div key={lote_id} className={`border rounded-lg p-3 flex items-center gap-3 transition-colors ${
                sel.checked ? "border-[#921A0E] bg-[#921A0E]/5" : "border-neutral-300 bg-white"
              }`} data-testid={`lote-sugerido-${lote_id}`}>
                <Checkbox
                  checked={sel.checked}
                  onCheckedChange={(v) => setSeleccion((prev) => ({
                    ...prev, [lote_id]: { ...sel, checked: !!v },
                  }))}
                  data-testid={`lote-checkbox-${lote_id}`}
                />
                <Badge className="bg-[#921A0E] text-white border-0 font-mono px-2 py-1">{sel.numero}</Badge>
                <div className="flex-1 min-w-0">
                  <div className="font-medium truncate">{sel.descripcion}</div>
                  <div className="text-xs text-neutral-500">
                    Elab {fmt(sel.fecha_elaboracion)} · Venc {fmt(sel.fecha_vencimiento)} · Disp: {sel.kilos_disponibles} kg
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  <NumberInput step="0.01" min="0"
                    max={sel.kilos_disponibles}
                    value={sel.kilos}
                    onChange={(e) => setSeleccion((prev) => ({
                      ...prev, [lote_id]: { ...sel, kilos: e.target.value },
                    }))}
                    disabled={!sel.checked}
                    className="w-24 text-right font-mono"
                    data-testid={`lote-kilos-${lote_id}`}
                  />
                  <span className="text-xs text-neutral-500">kg</span>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Buscador cross-producto */}
        <div className="pt-2 space-y-2">
          <div className="text-[11px] uppercase font-mono text-neutral-500">Buscar otro lote (cualquier producto)</div>
          <div className="relative">
            <MagnifyingGlass size={16} className="absolute left-2 top-1/2 -translate-y-1/2 text-neutral-400" />
            <Input
              value={buscarQ}
              onChange={(e) => setBuscarQ(e.target.value)}
              placeholder="Producto, código o número de lote…"
              className="pl-8"
              data-testid="lotes-buscador-input"
            />
          </div>
          {buscarLoading && <div className="text-xs text-neutral-400">Buscando…</div>}
          {!buscarLoading && lotesAlt.length > 0 && (
            <div className="border rounded-md max-h-40 overflow-y-auto divide-y" data-testid="lotes-alt-results">
              {lotesAlt.map((l) => (
                <button
                  key={l.id}
                  onClick={() => agregarLoteAlt(l)}
                  className="w-full text-left px-3 py-2 hover:bg-neutral-50 flex items-center gap-2 text-xs"
                  data-testid={`lote-alt-${l.id}`}
                >
                  <Plus size={14} className="text-emerald-600" />
                  <Badge variant="outline" className="font-mono text-[10px]">{l.numero}</Badge>
                  <span className="font-medium truncate flex-1">{l.descripcion}</span>
                  <span className="font-mono text-neutral-600">{l.kilos_disponibles} kg</span>
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="grid grid-cols-3 gap-2 pt-3 border-t border-neutral-200">
          <div className="border border-neutral-200 rounded-md p-2 text-center">
            <div className="text-[10px] uppercase font-mono text-neutral-500">Necesitás</div>
            <div className="text-lg font-bold font-mono">{kilosNecesarios} kg</div>
          </div>
          <div className={`border-2 rounded-md p-2 text-center ${totales.usados >= kilosNecesarios ? "border-emerald-300 bg-emerald-50" : "border-amber-300 bg-amber-50"}`}>
            <div className="text-[10px] uppercase font-mono text-neutral-600">Estás usando</div>
            <div className={`text-lg font-bold font-mono ${totales.usados >= kilosNecesarios ? "text-emerald-700" : "text-amber-700"}`}>{totales.usados} kg</div>
          </div>
          <div className="border border-neutral-200 rounded-md p-2 text-center">
            <div className="text-[10px] uppercase font-mono text-neutral-500">Faltan</div>
            <div className={`text-lg font-bold font-mono ${totales.faltan > 0 ? "text-rose-600" : "text-emerald-700"}`}>{totales.faltan} kg</div>
          </div>
        </div>

        {restanteBackend > 0 && (
          <div className="text-xs bg-amber-50 border border-amber-200 rounded-md p-2 text-amber-800">
            Con los lotes de <strong>{producto}</strong> no alcanzás a cubrir el pedido ({restanteBackend} kg sin materia prima).
            Podés buscar otro lote arriba, terminar igual o regularizarlo luego con una compra/transformación.
          </div>
        )}

        <div className="flex justify-between items-center gap-2 pt-3 border-t border-neutral-200">
          <div className="flex gap-2">
            <Button variant="outline" onClick={onCancel} data-testid="lotes-cancelar">Cancelar</Button>
            <Button variant="ghost" onClick={saltar} className="text-xs" data-testid="lotes-saltar">
              Terminar sin registrar lote
            </Button>
          </div>
          <Button
            onClick={confirmar}
            disabled={loading || Object.values(seleccion).every((v) => !v.checked)}
            className="gap-2 bg-emerald-600 hover:bg-emerald-700 text-white"
            data-testid="lotes-confirmar"
          >
            <CheckCircle size={16} weight="bold" /> Confirmar y Terminar
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
